
import { Component, Vue, Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import PModal from "@/components/look/PModal.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { IApprovalsParams } from "@/services";
import { SweetAlertResult } from "sweetalert2";
import { SourceRequestViewEnum } from "@/entities";

@Component({
  name: "approvals-export-modal",
  computed: {
    ...mapGetters(["getLocale"]),
  },
  components: {
    PModal,
    ValidationObserver,
    ValidationProvider,
  },
})
export default class ApprovalsExportModal extends Vue {
  orderDateFrom = "";
  orderDateTo = "";
  isLoading = false;

  constructor() {
    super();
  }

  @Watch("orderDateFrom")
  onOrderDateFromChange(): void {
    if (!this.orderDateTo) return;
    const from = new Date(`${this.orderDateFrom}T00:00:00`);
    const to = new Date(`${this.orderDateTo}T00:00:00`);
    if (from > to) {
      this.orderDateTo = this.toDateString(from);
    } else {
      const maxTo = this.addDays(from, 7);
      if (to > maxTo) {
        this.orderDateTo = this.toDateString(maxTo);
      }
    }
  }

  @Watch("orderDateTo")
  onOrderDateToChange(): void {
    if (!this.orderDateFrom) return;
    const from = new Date(`${this.orderDateFrom}T00:00:00`);
    const to = new Date(`${this.orderDateTo}T00:00:00`);
    if (to < from) {
      this.orderDateFrom = this.toDateString(to);
    } else {
      const minFrom = this.addDays(to, -7);
      if (from < minFrom) {
        this.orderDateFrom = this.toDateString(minFrom);
      }
    }
  }

  initData(): void {
    const today = new Date();
    this.orderDateFrom = this.toDateString(today);
    this.orderDateTo = this.toDateString(this.addDays(today, 7));
  }

  get locale(): string {
    return (this as any).getLocale || "en";
  }

  orderDateMin(): string {
    return this.toDateString(this.addDays(new Date(), -7));
  }

  orderDateMax(): string {
    return this.toDateString(this.addDays(new Date(), 7));
  }

  private toDateString(date: Date): string {
    return date.toISOString().split("T")[0];
  }

  private startOfDay(date: Date): string {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    return start.toISOString();
  }

  private endOfDay(date: Date): string {
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return end.toISOString();
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  async exportOrders(): Promise<void> {
    this.isLoading = true;

    const { isConfirmed } = await this.modalConfirm();
    if (!isConfirmed) {
      this.isLoading = false;
      return;
    }

    try {
      const params: IApprovalsParams = {
        orderServiceDateFrom: this.startOfDay(new Date(`${this.orderDateFrom}T00:00:00`)),
        orderServiceDateTo: this.endOfDay(new Date(`${this.orderDateTo}T00:00:00`)),
        sourceRequestView: SourceRequestViewEnum.APPROVALS,
      };
      await this.$services.approvals.exportOrders(params);
    } finally {
      this.isLoading = false;
    }
    this.$emit("close", { isNotified: true });
  }

  close(): void {
    this.$emit("close", { isNotified: false });
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("label.confirmation.to.export.message").toString(),
      text: this.$t("label.confirmation.to.export.message.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes").toString(),
      cancelButtonText: this.$t("label.no").toString(),
    });
  }
}
